/**
 * Extracts and normalizes individual date/time parts from a timestamp
 */
import { fromUnixTimestamp } from '@utils/date-manipulation/from-unix-timestamp';

import type { CustomDateTimeFormatOptions } from './extract-date-time-parts.type';

const getNormalizedWeekday = (weekday: string | undefined, isVeryShort: boolean): string => {
  let normalized = (weekday ?? '').substring(0, 3);
  normalized = normalized.replaceAll(/[,.\s]/g, ''); // Remove punctuation
  normalized = normalized.charAt(0).toUpperCase() + normalized.slice(1).toLowerCase();

  if (isVeryShort) {
    normalized = normalized.charAt(0);
  }
  return normalized;
};

const getNormalizedMonth = (monthStr: string | undefined, monthFormat: string): string => {
  let month = monthStr ?? '';
  month = month.replace(/[,.\s]/g, ''); // Remove punctuation

  if (monthFormat === 'short') {
    // Special case for French: "Juin" -> "juin"
    if (month !== 'juin' && month !== 'juil') {
      month = month.substring(0, 3);
    } else {
      month = `${month.substring(0, 2)}${month.substring(3, 4).toUpperCase()}`;
    }
  }
  return month.charAt(0).toUpperCase() + month.slice(1).toLowerCase();
};

const getNormalizedPeriod = (dayPeriod: string | undefined): string => {
  let period = (dayPeriod ?? '').toLowerCase();
  // Remove all dots, spaces, and non-breaking spaces
  period = period.replace(/[\s\u00A0\u202F.]/g, '');
  if (period.includes('a')) return 'am';
  if (period.includes('p')) return 'pm';
  return period;
};

/**
 * Extracts and normalizes individual date/time parts from a timestamp
 */
export const extractDateTimeParts = (
  timestamp: number,
  locale: string,
  timezone?: string,
  customFormatOptions?: CustomDateTimeFormatOptions
): {
  weekday: string;
  day: string;
  month: string;
  year: string;
  hour: string;
  hour24: string;
  minute: string;
  period: string;
} => {
  const date = fromUnixTimestamp(timestamp);

  // Separate custom options from native options to avoid passing invalid values to Intl
  // and to ensure we don't mix dateStyle with individual components if dateStyle is veryShort
  const { dateStyle, ...otherCustomOptions } = customFormatOptions ?? {};
  const isVeryShort = dateStyle === 'veryShort';

  const formatOptions: Intl.DateTimeFormatOptions = {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    ...(timezone && { timeZone: timezone }),
    ...otherCustomOptions,
    // Only pass dateStyle if it is NOT the custom veryShort value
    ...(dateStyle && !isVeryShort ? { dateStyle: dateStyle as Intl.DateTimeFormatOptions['dateStyle'] } : {}),
  };

  const formatter = new Intl.DateTimeFormat(locale, formatOptions);
  const parts = formatter.formatToParts(date);

  // Extract parts from the formatted output
  const partsMap: Record<string, string> = {};
  parts.forEach((part) => {
    partsMap[part.type] = part.value;
  });

  // Also extract 24-hour format
  const formatter24 = new Intl.DateTimeFormat(locale, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    ...(timezone && { timeZone: timezone }),
  });
  const parts24 = formatter24.formatToParts(date);
  const partsMap24: Record<string, string> = {};
  parts24.forEach((part) => {
    partsMap24[part.type] = part.value;
  });

  return {
    weekday: getNormalizedWeekday(partsMap.weekday, isVeryShort),
    day: partsMap.day || '',
    month: getNormalizedMonth(partsMap.month, formatOptions.month ?? 'short'),
    year: partsMap.year || '',
    hour: partsMap.hour || '',
    hour24: partsMap24.hour || '00',
    minute: (partsMap.minute || '00').padStart(2, '0'),
    period: getNormalizedPeriod(partsMap.dayPeriod),
  };
};
